import { apiService } from './api';
import { 
  Tenant, 
  TenantCreateRequest, 
  TenantStats, 
  SystemUser, 
  UserCreateRequest, 
  UserUpdateRequest,
  Permission,
  Role,
  PermissionMatrix,
  SystemConfig,
  LLMConfig,
  STTConfig,
  TTSConfig,
  ExternalAPIConfig,
  SystemAnalytics
} from '../types/admin.types';
import { FilterParams, PaginatedResponse } from '../types/api.types';

export const adminService = {
  // Tenant management
  getTenants: async (params?: FilterParams): Promise<PaginatedResponse<Tenant>> => {
    return await apiService.get<PaginatedResponse<Tenant>>('/admin/tenants/', params);
  },

  getTenant: async (id: string): Promise<Tenant> => {
    return await apiService.get<Tenant>(`/admin/tenants/${id}/`);
  },

  createTenant: async (data: TenantCreateRequest): Promise<Tenant> => {
    return await apiService.post<Tenant>('/admin/tenants/', data);
  },

  updateTenant: async (id: string, data: Partial<Tenant>): Promise<Tenant> => {
    return await apiService.patch<Tenant>(`/admin/tenants/${id}/`, data);
  },

  deleteTenant: async (id: string): Promise<void> => {
    await apiService.delete(`/admin/tenants/${id}/`);
  },

  getTenantStats: async (id: string): Promise<TenantStats> => {
    return await apiService.get<TenantStats>(`/admin/tenants/${id}/stats/`);
  },

  // User management
  getUsers: async (params?: FilterParams): Promise<PaginatedResponse<SystemUser>> => {
    return await apiService.get<PaginatedResponse<SystemUser>>('/admin/users/', params);
  },

  getUser: async (id: string): Promise<SystemUser> => {
    return await apiService.get<SystemUser>(`/admin/users/${id}/`);
  },

  createUser: async (data: UserCreateRequest): Promise<SystemUser> => {
    return await apiService.post<SystemUser>('/admin/users/', data);
  },

  updateUser: async (id: string, data: UserUpdateRequest): Promise<SystemUser> => {
    return await apiService.patch<SystemUser>(`/admin/users/${id}/`, data);
  },

  deleteUser: async (id: string): Promise<void> => {
    await apiService.delete(`/admin/users/${id}/`);
  },

  // Permission management
  getPermissions: async (): Promise<Permission[]> => {
    return await apiService.get<Permission[]>('/admin/permissions/');
  },

  getRoles: async (): Promise<Role[]> => {
    return await apiService.get<Role[]>('/admin/roles/');
  },

  createRole: async (data: Partial<Role>): Promise<Role> => {
    return await apiService.post<Role>('/admin/roles/', data);
  },

  updateRole: async (id: string, data: Partial<Role>): Promise<Role> => {
    return await apiService.patch<Role>(`/admin/roles/${id}/`, data);
  },

  deleteRole: async (id: string): Promise<void> => {
    await apiService.delete(`/admin/roles/${id}/`);
  },

  getPermissionMatrix: async (): Promise<PermissionMatrix[]> => {
    return await apiService.get<PermissionMatrix[]>('/admin/permission-matrix/');
  },

  updatePermissionMatrix: async (userId: string, permissions: { [key: string]: boolean }): Promise<void> => {
    await apiService.patch(`/admin/permission-matrix/${userId}/`, { permissions });
  },

  // System configuration
  getSystemConfig: async (): Promise<SystemConfig[]> => {
    return await apiService.get<SystemConfig[]>('/admin/system-config/');
  },

  updateSystemConfig: async (key: string, value: any): Promise<SystemConfig> => {
    return await apiService.patch<SystemConfig>(`/admin/system-config/${key}/`, { value });
  },

  // LLM Configuration
  getLLMConfigs: async (): Promise<LLMConfig[]> => {
    return await apiService.get<LLMConfig[]>('/admin/llm-configs/');
  },

  updateLLMConfig: async (provider: string, data: Partial<LLMConfig>): Promise<LLMConfig> => {
    return await apiService.patch<LLMConfig>(`/admin/llm-configs/${provider}/`, data);
  },

  testLLMConfig: async (provider: string, apiKeyOverride?: string): Promise<any> => {
    const body = apiKeyOverride ? { api_key: apiKeyOverride } : {};
    return await apiService.post<any>(`/admin/llm-configs/${provider}/test/`, body);
  },

  // STT Configuration
  getSTTConfigs: async (): Promise<STTConfig[]> => {
    return await apiService.get<STTConfig[]>('/admin/stt-configs/');
  },

  updateSTTConfig: async (provider: string, data: Partial<STTConfig>): Promise<STTConfig> => {
    return await apiService.patch<STTConfig>(`/admin/stt-configs/${provider}/`, data);
  },

  // TTS Configuration
  getTTSConfigs: async (): Promise<TTSConfig[]> => {
    return await apiService.get<TTSConfig[]>('/admin/tts-configs/');
  },

  updateTTSConfig: async (provider: string, data: Partial<TTSConfig>): Promise<TTSConfig> => {
    return await apiService.patch<TTSConfig>(`/admin/tts-configs/${provider}/`, data);
  },

  // External API Configuration
  getExternalAPIConfigs: async (): Promise<ExternalAPIConfig[]> => {
    return await apiService.get<ExternalAPIConfig[]>('/admin/external-api-configs/');
  },

  createExternalAPIConfig: async (data: ExternalAPIConfig): Promise<ExternalAPIConfig> => {
    return await apiService.post<ExternalAPIConfig>('/admin/external-api-configs/', data);
  },

  updateExternalAPIConfig: async (name: string, data: Partial<ExternalAPIConfig>): Promise<ExternalAPIConfig> => {
    return await apiService.patch<ExternalAPIConfig>(`/admin/external-api-configs/${name}/`, data);
  },

  deleteExternalAPIConfig: async (name: string): Promise<void> => {
    await apiService.delete(`/admin/external-api-configs/${name}/`);
  },

  // System analytics
  getSystemAnalytics: async (): Promise<SystemAnalytics> => {
    return await apiService.get<SystemAnalytics>('/admin/analytics/');
  },

  // System health
  getSystemHealth: async (): Promise<{ status: string; details: any }> => {
    return await apiService.get('/admin/health/');
  },

  // Audit logs
  getAuditLogs: async (params?: FilterParams): Promise<PaginatedResponse<any>> => {
    return await apiService.get<PaginatedResponse<any>>('/admin/audit-logs/', params);
  },
};